import type {
  IProviderConfig,
  IUserInfo,
  IVaultMedia,
} from "@/account/account-models";
import {
  createMediaHash,
  getMediaAvailable,
} from "@/common/uploader/media-uploader-helpers";
import type { IFileWrapper } from "@/common/uploader/uploader-helpers";
import {
  EFileType,
  getFileSizeInMegaBytes,
} from "@/common/uploader/uploader-helpers";
import { API_ROOT, IS_PROD } from "@/globals";
import type { IDictionary, IMediaType, IPopupConfig } from "@/models";
import { EPopupChatType } from "@/models";
import type { PubnubActions } from "@/pubnub/pubnub-actions";
import { getRequest, patchRequest, postRequest } from "@/utils/fetch";
import { createDirectMediaUpload } from "@/utils/media-upload";
import { sendWarningMessage } from "@/utils/slack";
import { timeStampInMs } from "@/utils/time";
import { defined } from "@/utils/variable-evaluation";
import type { NavigateFunction } from "react-router";
import type { ChatsActions } from "./chats-actions";
import type {
  EChatFilterCategory,
  IChatFilter,
  IGetChatsConfig,
} from "./chats-helpers";
import {
  EChatFilterType,
  getChatsUrl,
  setPresence,
  TO_API_VALUE,
  transformChatResponse,
} from "./chats-helpers";
import type {
  EBroadcastStage,
  IBroadcastMessage,
  IChatInfo,
  IChatTotals,
} from "./chats-models";
import { EBroadcastMessageType, EPatchUpdateChatsAction } from "./chats-models";

type IPopupConfirmDeleteChatsProps = import("react").ComponentProps<
  typeof import("@/common/popups/popup-chat/popup-confirm-delete-chats").PopupConfirmDeleteChats
>;
type IPopupConfirmHideChatsProps = import("react").ComponentProps<
  typeof import("@/common/popups/popup-chat/popup-confirm-hide-chats").PopupConfirmHideChats
>;

export function setFilters(
  filtersSet: IDictionary<IChatFilter>,
  user: IUserInfo,
  providerConfig: IProviderConfig,
  actions: ChatsActions,
  pubnubActions: PubnubActions,
  isSneakyReload?: boolean
) {
  getChats(actions, pubnubActions, {
    user,
    providerConfig,
    isInitialRequest: true,
    isSneakyReload: !!isSneakyReload,
    filtersSet,
  });
  actions.setFilters({ filtersSet });
}

export interface IGetChatsResponse {
  data: IChatInfo[];
  debug: string;
  next: string;
  prev: string;
  unread_messages: number;
}

export function getChats(
  actions: ChatsActions,
  pubnubActions: PubnubActions,
  config: IGetChatsConfig
) {
  const url = getChatsUrl(config);
  getChatTotals(actions);

  actions.initGetChats({ url, config });
  getRequest<IGetChatsResponse>(url).then((res) => {
    actions.successGetChats({
      res: transformChatResponse(res, config, pubnubActions),
      config,
    });
  }, actions.failureGetChats);
  // window['chatsLastFetchedAt'] = Date.now();
}

export function getNextChats(
  nextUrl: string,
  actions: ChatsActions,
  pubnubActions: PubnubActions,
  config: IGetChatsConfig
) {
  actions.initGetChats({ config });
  getRequest(API_ROOT + nextUrl).then(
    (res) =>
      actions.successGetChats({
        res: transformChatResponse(res, config, pubnubActions),
        config,
      }),
    actions.failureGetChats
  );
}

export function getChatTotals(actions: ChatsActions) {
  actions.initGetChatTotals();
  getRequest(`${API_ROOT}/v1/chats/total`).then(
    actions.successGetChatTotals,
    actions.failureGetChatTotals
  );
}

export function updateChatTotals(
  totals: Partial<IChatTotals>,
  actions: ChatsActions
) {
  actions.updateChatTotals(totals);
}

export function getAndAddChatToChatList(
  otherUserId: string,
  actions: ChatsActions,
  pubnubActions: PubnubActions
) {
  const url = `${API_ROOT}/v1/chat?recipient_id=${otherUserId}`;
  getRequest<IChatInfo>(url).then((res) => {
    setPresence(res, pubnubActions);
    getChatTotals(actions);
    addChatToChatList(res, actions);
  });
}

export function addChatToChatList(chat: IChatInfo, actions: ChatsActions) {
  actions.addChatToChatList({ chat });
}

export function removeChatFromChatList(
  otherUserId: string,
  actions: ChatsActions
) {
  actions.removeChatFromChatList({ otherUserId });
}

export function setChatPopup(
  config: IPopupConfig | null,
  actions: ChatsActions
) {
  actions.setChatPopup(config);
}

export function setChatEditing(isEditing: boolean, actions: ChatsActions) {
  actions.setChatEditing(isEditing);
}

export function toggleChatSelection(chatId: string, actions: ChatsActions) {
  actions.toggleChatSelection(chatId);
}

export function setAllChatsSelection(
  isSelectAll: boolean,
  actions: ChatsActions
) {
  actions.setAllChatsSelection(isSelectAll);
}

export function toggleAllChatsSelection(actions: ChatsActions) {
  actions.toggleAllChatsSelection();
}

function setPopupConfirmDeleteChats(
  isSelectAll: boolean,
  chatIds: string[],
  onConfirm: () => void,
  actions: ChatsActions
) {
  const onClose = () => setChatPopup(null, actions);
  const props: IPopupConfirmDeleteChatsProps = {
    isSelectAll,
    chatIds,
    onConfirm: () => {
      onConfirm();
      onClose();
    },
    onClose,
  };
  setChatPopup(
    { type: EPopupChatType.PopupConfirmDeleteChats, props },
    actions
  );
}

function setPopupConfirmHideChats(
  isSelectAll: boolean,
  chatIds: string[],
  onConfirm: () => void,
  actions: ChatsActions
) {
  const onClose = () => setChatPopup(null, actions);
  const props: IPopupConfirmHideChatsProps = {
    isSelectAll,
    chatIds,
    onConfirm: () => {
      onConfirm();
      onClose();
    },
    onClose,
  };
  setChatPopup({ type: EPopupChatType.PopupConfirmHideChats, props }, actions);
}

export function patchUpdateChats(
  action: EPatchUpdateChatsAction,
  isSelectAll: boolean,
  chatIds: string[],
  filtersSet: IDictionary<IChatFilter>,
  actions: ChatsActions,
  navigate: NavigateFunction
) {
  function doPatch() {
    actions.initPatchUpdateChats();
    patchRequest(`${API_ROOT}/v1/chats`, {
      action,
      [isSelectAll ? "except_ids" : "ids"]: chatIds.join(","),
      type:
        (TO_API_VALUE as any)[EChatFilterType.Category][
          filtersSet[EChatFilterType.Category].value
        ] || filtersSet[EChatFilterType.Category].value,
    }).then((res) => {
      actions.successPatchUpdateChats(res);
      if (
        action === EPatchUpdateChatsAction.HIDE ||
        action === EPatchUpdateChatsAction.DELETE
      ) {
        navigate("/chats");
      }
    }, actions.failurePatchUpdateChats);
  }
  if (action === EPatchUpdateChatsAction.DELETE) {
    setPopupConfirmDeleteChats(isSelectAll, chatIds, doPatch, actions);
  } else if (action === EPatchUpdateChatsAction.HIDE) {
    setPopupConfirmHideChats(isSelectAll, chatIds, doPatch, actions);
  } else {
    doPatch();
  }
}

export function setBroadcastStage(
  stage: EBroadcastStage,
  actions: ChatsActions
) {
  actions.setBroadcastStage({ stage });
}

export async function postBroadcastMessage(
  broadcastMessage: IBroadcastMessage,
  mediaFileWrapper: IFileWrapper | null,
  vaultMedia: IVaultMedia | null,
  user: IUserInfo,
  actions: ChatsActions
) {
  const __clientSideSendId =
    broadcastMessage.__clientSideSendId || timeStampInMs();

  if (broadcastMessage.type === EBroadcastMessageType.Text) {
    actions.initPostBroadcastMessage({
      clientSideBroadcastMessage: {
        ...broadcastMessage,
        __clientSideSendId,
      },
    });
    // postRequest(`${API_ROOT}/v1/auto-messages/send`, {
    //   ...broadcastMessage
    // }).then(
    //   (response) => {
    //     actions.successPostBroadcastMessage({
    //       response,
    //       __clientSideSendId
    //     });
    //     // getAutoMessagesReload(actions);
    //   },
    //   (errorResponse) => {
    //     actions.failurePostBroadcastMessage({
    //       errorResponse,
    //       __clientSideSendId
    //     });
    //   }
    // );

    actions.successPostBroadcastMessage({
      clientSideBroadcastMessage: {
        ...broadcastMessage,
        _id: "_" + __clientSideSendId, // pretending that the message is from API
        __clientSideSendId,
      },
    });
  } else if (mediaFileWrapper) {
    const md5Hash = createMediaHash(mediaFileWrapper!.file, user);

    // NOTE: In Feed Uploader there is only 1 request to upload to /v1/media
    // and it will automatically upload and create a new feed post
    // that's why we don't do this in feed, it's handled by API
    const response = await getMediaAvailable(
      md5Hash,
      broadcastMessage.price! > 0 ? "paid_message" : "message"
    );
    const isAvailable = response.status === true;

    if (isAvailable) {
      // this media was already uploaded before

      {
        const clientSideMedia: IMediaType = {
          public_id: md5Hash,
          th: mediaFileWrapper!.elm.src,
          st: mediaFileWrapper!.elm.src,
          rt: mediaFileWrapper!.elm.src,
          width: mediaFileWrapper!.width,
          height: mediaFileWrapper!.height,
          isClientSide: true,
          uploadProgress: 100,
          loaded: mediaFileWrapper!.file.size,
          total: mediaFileWrapper!.file.size,
        };

        if (mediaFileWrapper!.type === EFileType.Video) {
          clientSideMedia.video_url = mediaFileWrapper!.elm.src;
          clientSideMedia.video_length = mediaFileWrapper!.duration;
        }

        actions.initPostBroadcastMessage({
          clientSideBroadcastMessage: {
            ...broadcastMessage,
            __clientSideSendId,
            media: clientSideMedia,
            _id: undefined,
          },
        });
      }

      const { width, height, video_length, media } = response;

      {
        const clientSideMedia: IMediaType = {
          public_id: md5Hash,
          th: media ? media.th : mediaFileWrapper!.elm.src,
          st: media ? media.st : mediaFileWrapper!.elm.src,
          rt: media ? media.rt : mediaFileWrapper!.elm.src,
          sr: media ? media.sr : mediaFileWrapper!.elm.src,
          width,
          height,
          isClientSide: media ? false : true,
          abortUpload: undefined,
        };

        if (mediaFileWrapper!.type === EFileType.Video) {
          clientSideMedia.video_url = media
            ? media.video_url
            : mediaFileWrapper!.elm.src;
          clientSideMedia.video_length = video_length;
        }

        actions.successPostBroadcastMessage({
          clientSideBroadcastMessage: {
            ...broadcastMessage,
            _id: "_" + __clientSideSendId, // pretending that the message is from API
            __clientSideSendId,
            media: clientSideMedia,
          },
        });
      }
    } else {
      // need to upload first

      const upload = createDirectMediaUpload(mediaFileWrapper!.file, {
        md5: md5Hash,
        type: broadcastMessage.price! > 0 ? "paid_message" : "message",
      });

      const handleProgress = (
        progress: number,
        loaded: number,
        total: number
      ) => {
        // update upload progress
        actions.updatePostBroadcastMessageProgress({
          progress,
          loaded,
          total,
          __clientSideSendId,
        });
      };

      const handleSuccess = ({ response }: any) => {
        // send the message after upload
        const { width, height, video_length, media } = response;

        const clientSideMedia: IMediaType = {
          public_id: media.public_id,
          th: media.th,
          st: media.st,
          rt: media.rt,
          sr: media.sr,
          width,
          height,
          isClientSide: false,
          abortUpload: undefined,
        };

        if (mediaFileWrapper!.type === EFileType.Video) {
          clientSideMedia.video_url = media.video_url;
          clientSideMedia.video_length = video_length;
        }

        actions.successPostBroadcastMessage({
          clientSideBroadcastMessage: {
            ...broadcastMessage,
            _id: "_" + __clientSideSendId, // pretending that the message is from API
            __clientSideSendId,
            media: clientSideMedia,
          },
        });
      };

      const handleError = ({
        status,
        response,
        isAbort,
        step,
        attempt,
        key,
      }: any) => {
        if (!isAbort) {
          let errorMessage =
            "This file failed to upload. Please check your network connection and try again.";
          if (defined(response.message)) {
            errorMessage = response.message;
          }
          sendWarningMessage(
            `\`${
              window.username
            }\` Chats BroadcastMessagesUploader failed to upload: ${errorMessage} ${status} ${JSON.stringify(
              response
            )} ${JSON.stringify(mediaFileWrapper)} step: ${step} attempt: ${attempt} key: ${key} file mimeType: ${
              mediaFileWrapper?.file?.type
            } md5Hash: ${md5Hash} file size: ${getFileSizeInMegaBytes(mediaFileWrapper?.file)}`,
            IS_PROD && status >= 400 ? "#api-status" : "#web-logs"
          );
        }
        actions.failurePostBroadcastMessage({
          isAbort,
          errorResponse: response,
          __clientSideSendId,
        });
      };

      const clientSideMedia: IMediaType = {
        public_id: md5Hash,
        th: mediaFileWrapper!.elm.src,
        st: mediaFileWrapper!.elm.src,
        rt: mediaFileWrapper!.elm.src,
        width: mediaFileWrapper!.width,
        height: mediaFileWrapper!.height,
        isClientSide: true,
        uploadProgress: 0,
        loaded: 0,
        total: mediaFileWrapper!.file.size,
        abortUpload: () => {
          upload.abort();
        },
      };

      if (mediaFileWrapper!.type === EFileType.Video) {
        clientSideMedia.video_url = mediaFileWrapper!.elm.src;
        clientSideMedia.video_length = mediaFileWrapper!.duration;
      }

      actions.initPostBroadcastMessage({
        clientSideBroadcastMessage: {
          ...broadcastMessage,
          __clientSideSendId,
          media: clientSideMedia,
          _id: undefined,
        },
      });

      upload.send(handleProgress).then(handleSuccess, handleError);
    }
  } else if (vaultMedia) {
    const clientSideMedia: IMediaType = {
      public_id: vaultMedia.public_id,
      media_id: vaultMedia.media_id,
      th: vaultMedia.th,
      st: vaultMedia.st,
      rt: vaultMedia.rt,
      width: vaultMedia.width,
      height: vaultMedia.height,
      video_url: vaultMedia.video_url,
      video_length: vaultMedia.video_length,
      isClientSide: true,
      uploadProgress: 100,
      loaded: 0,
      total: 0,
    };

    actions.initPostBroadcastMessage({
      clientSideBroadcastMessage: {
        ...broadcastMessage,
        __clientSideSendId,
        media: clientSideMedia,
        _id: undefined,
      },
    });

    actions.successPostBroadcastMessage({
      clientSideBroadcastMessage: {
        ...broadcastMessage,
        _id: "_" + __clientSideSendId, // pretending that the message is from API
        __clientSideSendId,
        __isVaultMedia: true,
        media: clientSideMedia,
      },
    });
  }
}

// export async function setBroadcastMessageMedia(
//   broadcastMessage: IBroadcastMessage | null,
//   mediaFileWrapper: IFileWrapper | null,
//   user: IUserInfo,
//   actions: ChatsActions
// ) {
//   const __clientSideSendId = broadcastMessage.__clientSideSendId || timeStampInMs();

//   if (defined(mediaFileWrapper)) {
//     const md5Hash = createMediaHash(mediaFileWrapper.file, user);

//     const clientSideMedia: IMediaType = {
//       public_id: md5Hash,
//       th: mediaFileWrapper.elm.src,
//       st: mediaFileWrapper.elm.src,
//       rt: mediaFileWrapper.elm.src,
//       width: mediaFileWrapper.width,
//       height: mediaFileWrapper.height,
//       isClientSide: true,
//       uploadProgress: 0,
//       loaded: 0,
//       total: mediaFileWrapper.file.size,
//       abortUpload: undefined
//     };

//     if (mediaFileWrapper.type === EFileType.Video) {
//       clientSideMedia.video_url = mediaFileWrapper.elm.src;
//       clientSideMedia.video_length = mediaFileWrapper.duration;
//     }

//     actions.setBroadcastMessageMedia({
//       clientSideBroadcastMessage: {
//         ...broadcastMessage,
//         _id: '_' + md5Hash, // pretending that the message is from API
//         __clientSideSendId,
//         media: clientSideMedia,
//         mediaFileWrapper
//       }
//     });
//   } else {
//     actions.setBroadcastMessageMedia({
//       clientSideBroadcastMessage: null
//     });
//   }
// }

export function deleteBroadcastMessage(
  broadcastMessageId: string,
  actions: ChatsActions
) {
  actions.initDeleteBroadcastMessage({
    broadcastMessageId,
  });
  actions.successDeleteBroadcastMessage({
    broadcastMessageId,
  });
}

export async function postBroadcast(
  text: string,
  broadcastMediaMessage: IBroadcastMessage | null,
  filtersSet: IDictionary<IChatFilter>,
  chatsSelection: string[],
  isSelectAll: boolean,
  actions: ChatsActions
) {
  const category = filtersSet[EChatFilterType.Category]
    .value as EChatFilterCategory;

  actions.initPostBroadcast();
  postRequest(`${API_ROOT}/v1/broadcast/send`, {
    category:
      defined(TO_API_VALUE[EChatFilterType.Category]) &&
      defined((TO_API_VALUE[EChatFilterType.Category] as any)[category])
        ? (TO_API_VALUE[EChatFilterType.Category] as any)[category]
        : category,
    [isSelectAll ? "except_ids" : "ids"]: chatsSelection.join(","),
    type: broadcastMediaMessage
      ? broadcastMediaMessage.type
      : EBroadcastMessageType.Text,
    media_id: broadcastMediaMessage
      ? broadcastMediaMessage.media!.public_id
      : undefined,
    ...(broadcastMediaMessage?.__isVaultMedia
      ? {
          media_id: broadcastMediaMessage.media!.media_id,
          public_id: broadcastMediaMessage.media!.public_id,
        }
      : {}),
    price: broadcastMediaMessage ? broadcastMediaMessage.price : 0,
    price_in_dollars: broadcastMediaMessage
      ? broadcastMediaMessage.price_in_dollars
      : undefined,
    price_v2: broadcastMediaMessage?.price ? true : undefined,
    self_destruct: broadcastMediaMessage
      ? broadcastMediaMessage.self_destruct
      : false,
    self_destruct_hours: broadcastMediaMessage
      ? broadcastMediaMessage.self_destruct_hours
      : null,
    width: broadcastMediaMessage ? broadcastMediaMessage.media!.width : 0,
    height: broadcastMediaMessage ? broadcastMediaMessage.media!.height : 0,
    video_length: broadcastMediaMessage
      ? broadcastMediaMessage.media!.video_length
      : 0,
    text,
  }).then(actions.successPostBroadcast, actions.failurePostBroadcast);
}
